import { useState, type FormEvent } from 'react';
import {
  assertNever,
  CommitmentClosedReason,
  FUNDRAISER_CLOSE_REASONS,
  type CloseCommitmentRequest,
  type OpenCommitmentDto,
} from '@signalvote/shared';
import { Link } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { joined } from '@/lib/fundraising/prospectRecord';
import { cn } from '@/lib/utils';
import { daysBetween, shortDay } from './activityDay';

type FundraiserReason = (typeof FUNDRAISER_CLOSE_REASONS)[number];

// Committed amounts are whole-dollar in the list; the total keeps its cents.
const dollars = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const cents = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const count = new Intl.NumberFormat('en-US');

/** How far ahead "this week" reaches: today and the six days after it, on the campaign's calendar. */
const THIS_WEEK_DAYS = 6;

function reasonLabel(reason: FundraiserReason): string {
  switch (reason) {
    case CommitmentClosedReason.ReceivedElsewhere:
      return 'Received elsewhere';
    case CommitmentClosedReason.Withdrawn:
      return 'Withdrawn';
    case CommitmentClosedReason.Lapsed:
      return 'Lapsed';
    default:
      return assertNever(reason);
  }
}

type Group = 'pastDue' | 'thisWeek' | 'later';
const GROUPS: { group: Group; title: string; segment: string }[] = [
  { group: 'pastDue', title: 'Past expected date', segment: 'Past due' },
  { group: 'thisWeek', title: 'This week', segment: 'This week' },
  { group: 'later', title: 'Later', segment: 'Later' },
];

/** Where a commitment's expected-by date falls against the campaign's today. Undated ones are Later. */
function groupOf(commitment: OpenCommitmentDto, today: string): Group {
  if (!commitment.expectBy) return 'later';
  const days = daysBetween(today, commitment.expectBy);
  if (days < 0) return 'pastDue';
  return days <= THIS_WEEK_DAYS ? 'thisWeek' : 'later';
}

/** "7 days past", "Today", "In 3 days" — or nothing past this week. */
function dueNote(commitment: OpenCommitmentDto, today: string): { text: string; late: boolean } | null {
  if (!commitment.expectBy) return null;
  const days = daysBetween(today, commitment.expectBy);
  if (days < 0) return { text: `${-days} ${days === -1 ? 'day' : 'days'} past`, late: true };
  if (days === 0) return { text: 'Today', late: false };
  if (days <= THIS_WEEK_DAYS) return { text: `In ${days} ${days === 1 ? 'day' : 'days'}`, late: false };
  return null;
}

/** "Checked in Sep 28 · Jordan Ellis · check goes out Friday" — the latest check-in, under the person. */
function checkInLine(commitment: OpenCommitmentDto): string | null {
  const checkIn = commitment.latestCheckIn;
  if (!checkIn) return null;
  return [`Checked in ${shortDay(checkIn.on)}`, ...(checkIn.byName ? [checkIn.byName] : []), checkIn.note].join(' · ');
}

/** "Owner · Whitfield Foods · Montgomery" — what they do and where they live; null when nothing is known. */
function personLine({ occupation, employer, city }: OpenCommitmentDto): string | null {
  return joined(' · ', occupation, employer, city) || null;
}

/** The longest check-in note the server takes. */
const MAX_NOTE_LENGTH = 280;

interface OpenCommitmentsListProps {
  /** In the server's order: soonest expect-by first, undated last. */
  commitments: OpenCommitmentDto[];
  onClose: (id: string, request: CloseCommitmentRequest) => void;
  /** Log a check-in; rejects with the server's message on a refusal. */
  onCheckIn: (id: string, note: string) => Promise<unknown>;
  /** Set (`yyyy-MM-dd`) or clear (null) the expected date; rejects with the server's message on a refusal. */
  onChangeExpectBy: (id: string, expectBy: string | null) => Promise<unknown>;
  /** The commitment whose close is in flight, if any. */
  closingId: string | null;
  error: string | null;
  /** The campaign's today, `yyyy-MM-dd`, which the grouping reads. */
  today: string;
}

/**
 * The Fundraiser's chase list: what's committed and not yet received — a promise, never money, so never
 * added to money in — grouped by when it's expected on the campaign's calendar, with Close and its three
 * reasons. Each row takes a check-in (a short note) and a new expected date — annotations, never a state —
 * and shows its latest check-in; a row regroups when its date changes. Prop-driven off the real read contract.
 */
export function OpenCommitmentsList({
  commitments,
  onClose,
  onCheckIn,
  onChangeExpectBy,
  closingId,
  error,
  today,
}: OpenCommitmentsListProps) {
  const [shown, setShown] = useState<Group | null>(null);
  const grouped = new Map<Group, OpenCommitmentDto[]>(GROUPS.map(({ group }) => [group, []]));
  for (const commitment of commitments) grouped.get(groupOf(commitment, today))!.push(commitment);
  const total = commitments.reduce((sum, c) => sum + c.amount, 0);
  const [whole, fraction] = cents.format(total).split('.');
  const pastDue = grouped.get('pastDue')!.length;

  return (
    <div className="space-y-5">
      <section aria-labelledby="committed-total" className="space-y-1">
        <h2 id="committed-total" className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          Committed, not yet received
        </h2>
        <p className="text-[26px] font-bold leading-8 tabular-nums text-warning">
          {whole}
          <span className="text-base">.{fraction}</span>
        </p>
        <p className="text-xs text-muted-foreground">
          {count.format(commitments.length)} {commitments.length === 1 ? 'person' : 'people'} · {count.format(pastDue)} past their
          expected date
        </p>
      </section>
      <p className="max-w-2xl text-[13px] text-muted-foreground">
        A commitment is a promise, not money. Gifts that arrive are recorded in Finance. Closing a commitment here never
        records money.
      </p>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {commitments.length === 0 ? (
        <p className="text-sm text-muted-foreground">No open commitments.</p>
      ) : (
        <>
          <div role="group" aria-label="Which commitments" className="flex w-fit max-w-full gap-0.5 overflow-x-auto rounded-[10px] bg-muted p-[3px]">
            {[{ group: null, segment: 'All', size: commitments.length }, ...GROUPS.map((g) => ({ ...g, size: grouped.get(g.group)!.length }))].map(
              ({ group, segment, size }) => (
                <button
                  key={segment}
                  type="button"
                  aria-pressed={shown === group}
                  onClick={() => setShown(group)}
                  className={cn(
                    'flex h-[38px] items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-xs font-semibold transition-colors',
                    shown === group ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {segment}
                  <span className="font-medium tabular-nums text-muted-foreground">{count.format(size)}</span>
                </button>
              ),
            )}
          </div>

          <div className="overflow-hidden rounded-xl border bg-card">
            <div
              aria-hidden="true"
              className="hidden h-10 grid-cols-[minmax(0,2fr)_140px_90px_150px_minmax(0,1fr)_200px] items-center gap-4 px-5 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground md:grid"
            >
              <span>Person</span>
              <span className="text-right">Committed</span>
              <span>On</span>
              <span>Expected by</span>
              <span>Caller</span>
              <span />
            </div>
            {GROUPS.filter(({ group }) => (shown === null || shown === group) && grouped.get(group)!.length > 0).map(({ group, title }) => (
              <div key={group}>
                {shown === null && (
                  <p className="border-t bg-muted/40 px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                    {title}
                  </p>
                )}
                <ul aria-label={title}>
                  {grouped.get(group)!.map((commitment) => (
                    <CommitmentRow
                      key={commitment.id}
                      commitment={commitment}
                      today={today}
                      onClose={onClose}
                      onCheckIn={onCheckIn}
                      onChangeExpectBy={onChangeExpectBy}
                      closing={closingId === commitment.id}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

type Editing = 'checkIn' | 'date' | null;

function CommitmentRow({
  commitment,
  today,
  onClose,
  onCheckIn,
  onChangeExpectBy,
  closing,
}: {
  commitment: OpenCommitmentDto;
  today: string;
  onClose: OpenCommitmentsListProps['onClose'];
  onCheckIn: OpenCommitmentsListProps['onCheckIn'];
  onChangeExpectBy: OpenCommitmentsListProps['onChangeExpectBy'];
  closing: boolean;
}) {
  const due = dueNote(commitment, today);
  const checkIn = checkInLine(commitment);
  const person = personLine(commitment);
  const [editing, setEditing] = useState<Editing>(null);
  const [note, setNote] = useState('');
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const open = (next: Editing) => {
    setProblem(null);
    setNote('');
    setDate(commitment.expectBy ?? '');
    setEditing((was) => (was === next ? null : next));
  };
  const save = async (change: () => Promise<unknown>) => {
    setSaving(true);
    setProblem(null);
    try {
      await change();
      setEditing(null);
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'That didn’t save. Try again.');
    } finally {
      setSaving(false);
    }
  };
  const submitCheckIn = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = note.trim();
    if (trimmed) void save(() => onCheckIn(commitment.id, trimmed));
  };
  const submitDate = (event: FormEvent) => {
    event.preventDefault();
    if (date) void save(() => onChangeExpectBy(commitment.id, date));
  };

  return (
    <li
      aria-label={commitment.donorName}
      className="grid min-h-14 grid-cols-2 items-center gap-x-4 gap-y-1 border-t px-5 py-2 text-[13px] md:grid-cols-[minmax(0,2fr)_140px_90px_150px_minmax(0,1fr)_200px]"
    >
      <span className="min-w-0">
        <Link
          to="/admin/fundraising/prospects/$prospectId"
          params={{ prospectId: commitment.prospectId }}
          className="block truncate font-semibold text-foreground underline-offset-4 hover:underline"
        >
          {commitment.donorName}
        </Link>
        {person && <span className="block truncate text-xs text-muted-foreground">{person}</span>}
        {checkIn && <span className="block truncate text-xs text-muted-foreground">{checkIn}</span>}
      </span>
      <span className="text-right font-semibold tabular-nums text-warning">{dollars.format(commitment.amount)} committed</span>
      <span className="tabular-nums text-muted-foreground">{shortDay(commitment.committedOn)}</span>
      <span className="tabular-nums">
        <button
          type="button"
          onClick={() => open('date')}
          aria-label={`Change expected date for ${commitment.donorName}`}
          aria-expanded={editing === 'date'}
          className="block min-h-6 text-left underline-offset-4 hover:underline"
        >
          {commitment.expectBy ? shortDay(commitment.expectBy) : <span className="text-muted-foreground">No date</span>}
        </button>
        {due && <span className={cn('block text-xs', due.late ? 'text-destructive' : 'text-muted-foreground')}>{due.text}</span>}
      </span>
      <span className="truncate text-muted-foreground">{commitment.callerName ?? '—'}</span>
      <span className="col-span-2 flex items-center justify-end gap-1 md:col-span-1">
        <Button variant="ghost" className="h-10" aria-expanded={editing === 'checkIn'} onClick={() => open('checkIn')}>
          Check in
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="h-10" disabled={closing} aria-label={`Close ${commitment.donorName}’s commitment`}>
              {closing ? 'Closing…' : 'Close'}
              <ChevronDown className="ml-1 h-4 w-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Why? A closed commitment stays closed.</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {FUNDRAISER_CLOSE_REASONS.map((reason) => (
              <DropdownMenuItem
                key={reason}
                onSelect={() => onClose(commitment.id, { reason, concurrencyVersion: commitment.concurrencyVersion })}
              >
                {reasonLabel(reason)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </span>

      {editing === 'checkIn' && (
        <form onSubmit={submitCheckIn} className="col-span-full flex flex-wrap items-center gap-2 pb-1 pt-2">
          <Input
            autoFocus
            aria-label={`Check-in note for ${commitment.donorName}`}
            placeholder="What did you hear? e.g. check goes out Friday"
            value={note}
            maxLength={MAX_NOTE_LENGTH}
            onChange={(e) => setNote(e.target.value)}
            className="h-10 min-w-0 flex-1"
          />
          <Button type="submit" className="h-10" disabled={saving || !note.trim()}>
            Save check-in
          </Button>
          <Button type="button" variant="ghost" className="h-10" onClick={() => setEditing(null)}>
            Cancel
          </Button>
          <span className="basis-full text-xs text-muted-foreground">A check-in is kept as written. It doesn’t change the commitment.</span>
          {problem && <span className="basis-full text-xs text-destructive">{problem}</span>}
        </form>
      )}
      {editing === 'date' && (
        <form onSubmit={submitDate} className="col-span-full flex flex-wrap items-center gap-2 pb-1 pt-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            Expected by
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 w-44" />
          </label>
          <Button type="submit" className="h-10" disabled={saving || !date}>
            Save date
          </Button>
          {commitment.expectBy && (
            <Button type="button" variant="ghost" className="h-10" disabled={saving} onClick={() => void save(() => onChangeExpectBy(commitment.id, null))}>
              Clear date
            </Button>
          )}
          <Button type="button" variant="ghost" className="h-10" onClick={() => setEditing(null)}>
            Cancel
          </Button>
          {problem && <span className="basis-full text-xs text-destructive">{problem}</span>}
        </form>
      )}
    </li>
  );
}
